import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, MousePointerClick, ShieldCheck, Target, Users } from "lucide-react";
import api from "@/lib/api";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import { useAuth } from "@/context/AuthContext";

/**
 * Staff-only analytics page.
 * Shows the hard-data funnel: clicks → member growth, site-wide and per-server.
 *
 * Nothing here is exposed publicly — every endpoint is gated behind
 * get_admin_user on the backend which permits admin AND owner roles.
 */
export default function AdminStats() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [top, setTop] = useState([]);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!user || (user.role !== "admin" && user.role !== "owner")) {
      navigate("/");
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || (user.role !== "admin" && user.role !== "owner")) return;
    let alive = true;
    (async () => {
      try {
        const [ov, tp] = await Promise.all([
          api.get("/admin/stats/overview"),
          api.get("/admin/stats/servers", { params: { limit: 25 } }),
        ]);
        if (!alive) return;
        setOverview(ov.data);
        setTop(tp.data.items || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load stats");
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [user]);

  if (loading || !user) return null;
  if (user.role !== "admin" && user.role !== "owner") return null;

  return (
    <div className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="admin-stats-page">
      <SEO title="Staff analytics" description="Serverlistr staff analytics." path="/admin/stats" noindex />
      <Breadcrumbs items={[{ name: "Staff console", href: "/admin" }, { name: "Stats" }]} />

      <button
        onClick={() => navigate("/admin")}
        className="mt-2 inline-flex items-center gap-1 text-sm text-zinc-400 hover:text-white"
      >
        <ArrowLeft className="w-4 h-4" /> Back to staff console
      </button>

      <div className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-neon-pink">
        <ShieldCheck className="w-3.5 h-3.5" /> Staff-only · hard data
      </div>
      <h1 className="mt-2 font-heading text-3xl sm:text-4xl font-black">Site impact</h1>
      <p className="mt-2 text-sm text-zinc-400 max-w-2xl">
        Real numbers from Serverlistr itself. Views are page loads on server
        listings; clicks are Join-button presses on our site. We don&apos;t include
        Discord member growth here — that stat mixes joins from other sources.
      </p>

      {busy ? (
        <div className="mt-10 text-sm text-zinc-500">Loading numbers…</div>
      ) : (
        <>
          <section className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="stats-cards">
            <StatCard
              icon={<Eye className="w-4 h-4" />}
              label="Views (30d)"
              value={overview?.views_30d ?? 0}
              hint="Listing page loads on Serverlistr"
            />
            <StatCard
              icon={<MousePointerClick className="w-4 h-4" />}
              label="Join clicks (30d)"
              value={overview?.clicks_30d ?? 0}
              accent
            />
            <StatCard
              icon={<Target className="w-4 h-4" />}
              label="Join rate (30d)"
              value={formatPct(overview?.conversion_30d)}
              hint="Clicks ÷ views"
            />
            <StatCard
              icon={<MousePointerClick className="w-4 h-4" />}
              label="Join clicks (all-time)"
              value={overview?.clicks_all_time ?? 0}
            />
            <StatCard
              icon={<Users className="w-4 h-4" />}
              label="Live servers"
              value={overview?.servers_live ?? 0}
            />
          </section>

          <ClicksSparkline data={overview?.clicks_by_day || []} />

          <section className="mt-10" data-testid="top-servers">
            <div className="flex items-end justify-between gap-3 flex-wrap">
              <div>
                <h2 className="font-heading text-xl sm:text-2xl font-black">Top performers (30d)</h2>
                <p className="text-sm text-zinc-500 mt-1">Servers sorted by Join clicks. Hard proof each listing gets traffic.</p>
              </div>
            </div>
            {top.length === 0 ? (
              <div className="mt-6 rounded-2xl border border-white/10 bg-[#0A0A0F] p-8 text-center text-sm text-zinc-500">
                No click data yet. Numbers will populate as visitors start hitting Join.
              </div>
            ) : (
              <div className="mt-6 rounded-2xl border border-white/10 bg-[#0A0A0F] overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-xs uppercase tracking-widest text-zinc-500">
                    <tr>
                      <th className="text-left px-4 py-3 font-semibold">#</th>
                      <th className="text-left px-4 py-3 font-semibold">Server</th>
                      <th className="text-right px-4 py-3 font-semibold">Members</th>
                      <th className="text-right px-4 py-3 font-semibold">Views 30d</th>
                      <th className="text-right px-4 py-3 font-semibold">Clicks 30d</th>
                      <th className="text-right px-4 py-3 font-semibold">Join rate</th>
                      <th className="text-right px-4 py-3 font-semibold">Clicks all-time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {top.map((s, i) => (
                      <tr
                        key={s.server_id}
                        className="border-t border-white/5 hover:bg-white/[0.03] transition-colors"
                        data-testid={`stats-row-${s.server_id}`}
                      >
                        <td className="px-4 py-3 font-mono text-zinc-500">{i + 1}</td>
                        <td className="px-4 py-3">
                          {s.slug ? (
                            <Link
                              to={`/servers/${s.slug}`}
                              className="inline-flex items-center gap-2 hover:text-white"
                            >
                              {s.icon_url && (
                                <img src={s.icon_url} alt="" className="w-6 h-6 rounded" />
                              )}
                              <span className="font-semibold">{s.name}</span>
                            </Link>
                          ) : (
                            <span className="text-zinc-500 italic">{s.name}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-zinc-300">
                          {s.member_count.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-zinc-300">
                          {s.views_30d.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-white font-semibold">
                          {s.clicks_30d.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-neon-cyan">
                          {formatPct(s.conversion_30d)}
                        </td>
                        <td className="px-4 py-3 text-right tabular-nums text-zinc-400">
                          {s.clicks_total.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <p className="mt-8 text-xs text-zinc-600 max-w-2xl leading-relaxed">
            Every number here is first-party — collected on serverlistr.net itself.
            Views count page loads on server listings; clicks count Join-button
            presses. Join rate = clicks ÷ views.
          </p>
        </>
      )}
    </div>
  );
}

function formatPct(v) {
  const pct = (v || 0) * 100;
  if (!Number.isFinite(pct)) return "0%";
  if (pct === 0) return "0%";
  return `${pct.toFixed(pct >= 10 ? 0 : 1)}%`;
}

function StatCard({ icon, label, value, accent, hint }) {
  return (
    <div
      className={`rounded-2xl border p-4 ${
        accent
          ? "border-neon-pink/30 bg-neon-pink/[0.03]"
          : "border-white/10 bg-[#0A0A0F]"
      }`}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
        <span className="text-zinc-400">{icon}</span> {label}
      </div>
      <div
        className={`mt-2 font-heading text-2xl sm:text-3xl font-black tabular-nums ${
          accent ? "text-neon-pink" : "text-white"
        }`}
      >
        {typeof value === "number" ? value.toLocaleString() : value}
      </div>
      {hint && <div className="mt-1 text-[10px] text-zinc-500">{hint}</div>}
    </div>
  );
}

/** Small inline SVG sparkline showing clicks over the last 30 days.
 * Zero-config, no external chart lib needed. */
function ClicksSparkline({ data }) {
  const points = useMemo(() => {
    if (!data.length) return "";
    const w = 800;
    const h = 120;
    const pad = 8;
    const maxY = Math.max(1, ...data.map((d) => d.clicks));
    const step = data.length > 1 ? (w - pad * 2) / (data.length - 1) : 0;
    return data
      .map((d, i) => {
        const x = pad + i * step;
        const y = h - pad - (d.clicks / maxY) * (h - pad * 2);
        return `${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(" ");
  }, [data]);

  if (!data.length) {
    return (
      <div className="mt-6 rounded-2xl border border-white/10 bg-[#0A0A0F] p-6 text-sm text-zinc-500">
        No clicks logged yet in the last 30 days.
      </div>
    );
  }
  return (
    <div className="mt-6 rounded-2xl border border-white/10 bg-[#0A0A0F] p-4" data-testid="clicks-sparkline">
      <div className="text-xs uppercase tracking-widest text-zinc-500 font-bold mb-2">
        Daily Join clicks (last 30d)
      </div>
      <svg viewBox="0 0 800 120" className="w-full h-24" preserveAspectRatio="none">
        <polyline
          fill="none"
          stroke="rgb(88 101 242)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
          points={points}
        />
      </svg>
    </div>
  );
}
